import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import Link from "@material-ui/core/Link";
import LogoSquare from '../assets/plagaware-logo-square-50x50.png';
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import Service from "../services/service";
import { showCaptcha, config, resolveNotificationMessage } from '../helpers/constants';
import { FormHelperText, Paper, Grid, Box, Typography, Button, TextField } from "@material-ui/core";
import FullScreenPage from "./FullScreenPage";
import PasteDialog from "../components/PasteDialog";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 3),
    height: 48
  },
}));

export default function Register(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const classes = useStyles();
  const [userDetail, setUserDetail] = React.useState({
    userName: '',
    password: '',
    license: '',
    regCode: '',
    regMailSent: false
  });
  const [licenseVisible, setLicenseVisible] = React.useState(false);
  const [sendCodeDelay, setSendCodeDelay] = React.useState(30);
  const [sendAttempts, setSendAttempts] = React.useState(0);

  const s = state.strings;
  const { lc } = useParams();

  useEffect(() => {
    if (lc && !userDetail.license) {
      setLicenseVisible(true);
      setUserDetail({ ...userDetail, license: lc });
    }
    if (!state.user.loggedIn) {
      showCaptcha();
    }
  }, []);

  useEffect(() => {
    const id = setInterval(sendTimer, 1000);
    return () => { clearInterval(id) };
  }, [sendCodeDelay]);


  const sendTimer = () => {
    setSendCodeDelay(Math.max(0, sendCodeDelay - 1));
  }



  const captchaAction = (action, fn) => {
    const captchas = document.getElementsByClassName("grecaptcha-badge");
    if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
          Service.verifyReCaptcha(token).then((verifyRes) => {
            if (verifyRes.success) {
              fn();
            } else {
              dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
            }
          });
        });
      });
    } else {
      fn();
    }
  }

  const forwardToDashboard = (res) => {
    if (res.message) {
      dispatch(actions.openConfirmationDialog({
        title: s.notificationMessage.approvalStartedTitle,
        hint: s.notificationMessage.accountApprovalStartedHint,
        text: resolveNotificationMessage(res.message, s.notificationMessage)
      }));
    }
    Service.getUserInfo().then(getInfoRes => {
      dispatch(actions.setUserDetails(getInfoRes.data));
      dispatch(actions.navigate_to("/dashboard"));
      dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedIn.format(getInfoRes.data.mail), status: true }));
    });
  }

  const doRegister = (code) => {
    Service.registerUser(userDetail.userName, userDetail.password, userDetail.license, code || userDetail.regCode).then(res => {
      if (res.success && res.data && res.data.UserId) {
        forwardToDashboard(res);
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
        setUserDetail({ ...userDetail, regCode: "" });
      }
    });
  }

  const doSendActivationCode = () => {
    Service.registerUser(userDetail.userName, userDetail.password, userDetail.license).then(res => {
      if (res.success) {
        if (res.data && res.data.code) {  // No activation required, register with activation code sent by backend
          doRegister(res.data.code)
        } else {
          // activation required
          dispatch(actions.openToast({ type: "info", message: resolveNotificationMessage(res.message, s.notificationMessage), status: true }));
          setUserDetail({ ...userDetail, regCode: "", regMailSent: true });
          setSendCodeDelay(30);
          setSendAttempts(sendAttempts + 1);
        }
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }


  const handleRegister = () => {
    captchaAction("register", doRegister);
  }

  const handleSendActivationCode = () => {
    captchaAction("sendactivationcode", doSendActivationCode);
  }


  const handleEditMailAddress = () => {
    setUserDetail({ ...userDetail, regCode: "", regMailSent: false });
  }

  const handleResendCode = () => {
    doSendActivationCode();
  }


  const doGuestLogin = () => {
    dispatch(actions.showFullscreenLoading(true, s.loginDialog.creatingGuestAccount));
    Service.createGuestUser().then(res => {
      if (res.success) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          dispatch(actions.navigate_to("/dashboard"));
          dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedIn.format(getInfoRes.data.mail), status: true }));
          dispatch(actions.showFullscreenLoading(false));
        });
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
        dispatch(actions.showFullscreenLoading(false));
      }
    });
  }

  const handleGuestLogin = () => {
    captchaAction("guestLogin", doGuestLogin);
  }

  const handleChange = (value, key) => {
    setUserDetail({
      ...userDetail,
      [key]: value
    })
  }

  const showSSOHelp = () => {
    dispatch(actions.openPasteDialog({
      title: s.loginDialog.ssoLoginWithOrganization,
      hint: s.loginDialog.ssoLoginHint,
      description: s.loginDialog.ssoDescription,
      placeholder: s.loginDialog.emailPlaceholder,
      prefill: userDetail.userName,
      okLabel: s.ok,
      cancelLabel: s.cancel,
      onConfirm: onForwardToSSO
    }));
  }

  const onForwardToSSO = (payload, mail) => {
    Service.getSSOInfoFromMail(mail).then(res => {
      if (res.success) {
        dispatch(actions.navigate_to("/login/" + res.data));
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  return (

    <FullScreenPage>
      <PasteDialog />
      <Container component="main" style={{ width: 450 }}>
        <Paper className={classes.paper}>
          <img src={LogoSquare} alt="logo" style={{ margin: 16 }} />
          <Typography component="h1" variant="h5" paragraph>
            {s.loginDialog.registerHeading}
          </Typography>
          {(userDetail.regMailSent ?
            <>
              <Typography variant="body2" color="secondary" >{s.loginDialog.activationSent.format(userDetail.userName)}</Typography>
              <form>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  label={s.loginDialog.activationCode}
                  autoFocus
                  value={userDetail.regCode}
                  onChange={(evt) => handleChange(evt.target.value, 'regCode')} /></form>
              <Grid container direction="row" justifyContent="center">
                <Grid item xs>
                  {(sendAttempts > 2 && sendCodeDelay > 0) ? <Typography variant="body2">{s.loginDialog.resendCode} (in {sendCodeDelay})</Typography> :
                    <Link href="#" variant="body2" onClick={() => handleResendCode()}>
                      {s.loginDialog.resendCode}
                    </Link>}
                </Grid>
                <Grid item>
                  <Link href="#" variant="body2" onClick={() => handleEditMailAddress()}>
                    {s.loginDialog.back}
                  </Link>
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={userDetail.regCode.length != 4}
                className={classes.submit}
                onClick={handleRegister}>
                {s.loginDialog.activateAccount}
              </Button>
            </>
            :
            <>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={s.loginDialog.mail}
                name="email"
                autoComplete="email"
                autoFocus
                value={userDetail.userName}
                onChange={(evt) => handleChange(evt.target.value, 'userName')} />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={s.loginDialog.pwd}
                type="password"
                id="password"
                autoComplete="current-password"
                value={userDetail.password}
                onChange={(evt) => handleChange(evt.target.value, 'password')} />
              {(!licenseVisible) && <><Box mt={1} /><Typography variant="body2" color="secondary" style={{ cursor: "pointer" }} onClick={() => setLicenseVisible(!licenseVisible)}>{s.loginDialog.alreadyUsingPlagAware}</Typography></>}
              {licenseVisible && <>
                <TextField label={s.common.licenseCode}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  value={userDetail.license}
                  onChange={(e) => handleChange(e.target.value, 'license')}
                />
                <FormHelperText>{s.loginDialog.enterLicenseCode}</FormHelperText>
              </>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={!userDetail.userName || !userDetail.password || (userDetail.password.length < 5) || (userDetail.userName.length < 6)}
                className={classes.submit}
                onClick={handleSendActivationCode}>
                {s.loginDialog.register}
              </Button>
            </>)}


          <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2" onClick={() => dispatch(actions.navigate_to('/reset'))}>
                {s.loginDialog.forgotPwd}
              </Link>
            </Grid>
            <Grid item>
              <Link href="#" variant="body2" onClick={() => dispatch(actions.navigate_to('/login'))}>
                {s.loginDialog.loginLink}
              </Link>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2" onClick={() => showSSOHelp()}>
                {s.loginDialog.ssoLoginWithOrganization}
              </Link>
            </Grid>
            <Grid item>
              {state.user.loggedIn ?
                <Link href="#" variant="body2" onClick={() => dispatch(actions.navigate_to('/dashboard'))}>
                  {s.loginDialog.continueAs.format(state.user.mail)}
                </Link> :
                <Link href="#" variant="body2" onClick={handleGuestLogin}>
                  {s.loginDialog.guest}
                </Link>
              }
            </Grid>
          </Grid>
        </Paper>
        <Box mt={2} />
        <Typography variant="caption" align="center" style={{ marginTop: 20, color: '#aaaaaa' }}>
          {s.loginDialog.acceptTerms}
        </Typography>
      </Container>
    </FullScreenPage>);
}
